import { ApiError, NetworkError, type ApiErrorBody } from './errors';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  sessionId: string;
}

export interface HttpClientOptions {
  /** Base URL including the API prefix, e.g. `http://localhost:3000/api`. */
  baseUrl: string;
  /** Private API token; when set it replaces bearer authentication. */
  apiToken?: string;
  /** Preferred locale, sent as `X-Locale`. */
  locale?: string;
  /** Called whenever a refresh produces a new token pair. */
  onTokensRefreshed?: (tokens: AuthTokens) => void;
  /** Called when refreshing fails and the session is gone. */
  onUnauthorized?: () => void;
  /**
   * Reads the last persisted pair. Consulted just before rotating, so a
   * rotation performed by another tab is adopted instead of replayed.
   */
  loadTokens?: () => Pick<AuthTokens, 'accessToken' | 'refreshToken'> | null;
  /** Request timeout in milliseconds. */
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  method: string;
  path: string;
  body?: unknown;
  query?: Record<string, unknown> | undefined;
  init?: RequestInit;
  /** Skip the automatic refresh-and-retry (used by the refresh call itself). */
  skipAuthRetry?: boolean;
}

/**
 * Transport used by every generated API method.
 *
 * Handles authentication, transparent refresh-token rotation, query
 * serialisation, `FormData` uploads and error normalisation.
 */
export class HttpClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private recoveryInFlight: Promise<boolean> | null = null;
  private readonly fetchImpl: typeof fetch;

  constructor(private options: HttpClientOptions) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  setTokens(tokens: Pick<AuthTokens, 'accessToken' | 'refreshToken'> | null): void {
    this.accessToken = tokens?.accessToken ?? null;
    this.refreshToken = tokens?.refreshToken ?? null;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  setLocale(locale: string): void {
    this.options.locale = locale;
  }

  setApiToken(token: string | undefined): void {
    this.options.apiToken = token;
  }

  get baseUrl(): string {
    return this.options.baseUrl;
  }

  async request<T>(options: RequestOptions): Promise<T> {
    let response = await this.send(options);

    // Two recoveries at most: adopting a pair another tab rotated may hand us
    // an access token that has itself expired, and that one still deserves a
    // rotation before the session is declared dead.
    for (let attempt = 0; attempt < 2 && this.needsRecovery(response, options); attempt += 1) {
      if (!(await this.recoverSession())) break;
      response = await this.send(options);
    }

    if (this.needsRecovery(response, options)) {
      // Drop the dead credential before handing over, so the next request —
      // typically the user signing back in — is not sent with it.
      this.setTokens(null);
      this.options.onUnauthorized?.();
    }

    return this.parse<T>(response);
  }

  private needsRecovery(response: Response, options: RequestOptions): boolean {
    if (response.status !== 401 || options.skipAuthRetry) return false;
    return !!this.accessToken || !!this.refreshToken;
  }

  private async send(options: RequestOptions): Promise<Response> {
    const url = new URL(this.join(options.path), this.options.baseUrl.endsWith('/') ? this.options.baseUrl : `${this.options.baseUrl}/`);

    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, String(item));
      } else {
        url.searchParams.set(key, String(value));
      }
    }

    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers: Record<string, string> = {
      accept: 'application/json',
      ...(isFormData ? {} : options.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(this.options.locale ? { 'x-locale': this.options.locale } : {}),
      ...((options.init?.headers as Record<string, string>) ?? {}),
    };

    if (this.options.apiToken) headers['x-api-token'] = this.options.apiToken;
    else if (this.accessToken) headers.authorization = `Bearer ${this.accessToken}`;

    const controller = new AbortController();
    const timeout = this.options.timeoutMs
      ? setTimeout(() => controller.abort(), this.options.timeoutMs)
      : null;

    try {
      return await this.fetchImpl(url.toString(), {
        ...options.init,
        method: options.method,
        headers,
        body: options.body === undefined ? undefined : isFormData ? (options.body as FormData) : JSON.stringify(options.body),
        signal: options.init?.signal ?? controller.signal,
        credentials: options.init?.credentials ?? 'omit',
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw new NetworkError('The request timed out', error);
      }
      throw new NetworkError('The request could not be sent', error);
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }

  private async parse<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');

    if (!response.ok) {
      const body = isJson
        ? ((await response.json().catch(() => null)) as ApiErrorBody | null)
        : null;
      throw new ApiError(
        body ?? {
          statusCode: response.status,
          code: response.status === 404 ? 'not_found' : 'internal_error',
          message: await response.text().catch(() => response.statusText),
          timestamp: new Date().toISOString(),
        },
      );
    }

    if (!isJson) {
      /**
       * An empty body is nothing, not an empty file.
       *
       * A handler returning `null` or `undefined` makes Nest send 200 with no
       * body and no content type, which lands here and used to become a
       * zero-byte `Blob` — an object, and therefore *truthy*. Callers testing
       * the result for existence all got yes and then read `undefined` off it.
       * Downloads are unaffected: they have bytes.
       */
      const blob = await response.blob();
      return (blob.size === 0 ? undefined : blob) as T;
    }
    return (await response.json()) as T;
  }

  /**
   * Rotates the pair without a 401 to react to.
   *
   * The realtime socket needs this: it is refused by the gateway rather than
   * answered with a status code, so it has no response to drive the usual
   * recovery. Shares the in-flight attempt with the request path.
   */
  refreshSession(): Promise<boolean> {
    return this.recoverSession();
  }

  /** Recovers the session once; concurrent callers share the same attempt. */
  private async recoverSession(): Promise<boolean> {
    if (this.recoveryInFlight) return this.recoveryInFlight;

    this.recoveryInFlight = this.adoptOrRefresh().finally(() => {
      this.recoveryInFlight = null;
    });
    return this.recoveryInFlight;
  }

  /**
   * Another tab may already have rotated the pair. Presenting our superseded
   * copy is indistinguishable from a stolen refresh token, and the server
   * answers by revoking the session for *every* tab — so the persisted pair
   * always wins over the one held in memory.
   */
  private async adoptOrRefresh(): Promise<boolean> {
    const persisted = this.options.loadTokens?.();
    if (persisted?.refreshToken && persisted.refreshToken !== this.refreshToken) {
      this.accessToken = persisted.accessToken;
      this.refreshToken = persisted.refreshToken;
      return true;
    }
    return this.rotate();
  }

  /** Exchanges the refresh token for a new pair. */
  private async rotate(): Promise<boolean> {
    if (!this.refreshToken) return false;
    try {
      const response = await this.send({
        method: 'POST',
        path: 'auth/refresh',
        body: { refreshToken: this.refreshToken },
        skipAuthRetry: true,
      });
      if (!response.ok) return false;

      const payload = (await response.json()) as { tokens: AuthTokens | null };
      if (!payload.tokens) return false;

      this.accessToken = payload.tokens.accessToken;
      this.refreshToken = payload.tokens.refreshToken;
      this.options.onTokensRefreshed?.(payload.tokens);
      return true;
    } catch {
      return false;
    }
  }

  private join(path: string): string {
    return path.startsWith('/') ? path.slice(1) : path;
  }

  /** Convenience helpers for hand-written calls and file downloads. */
  get<T>(path: string, query?: Record<string, unknown>): Promise<T> {
    return this.request<T>({ method: 'GET', path, query });
  }

  post<T>(path: string, body?: unknown, query?: Record<string, unknown>): Promise<T> {
    return this.request<T>({ method: 'POST', path, body, query });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>({ method: 'PATCH', path, body });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>({ method: 'PUT', path, body });
  }

  delete<T>(path: string, query?: Record<string, unknown>): Promise<T> {
    return this.request<T>({ method: 'DELETE', path, query });
  }

  /** Absolute URL for an API path — useful for `<img src>` and downloads. */
  url(path: string, query?: Record<string, unknown>): string {
    const url = new URL(this.join(path), this.options.baseUrl.endsWith('/') ? this.options.baseUrl : `${this.options.baseUrl}/`);
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }
    return url.toString();
  }
}
